import getSlug from "speakingurl";
import { ACTIVE, DIGITAL } from "../../enums";
import { ByStoreType, CSSEvent, TimeSlot } from "../../types/model";
import { toTimestamp } from "../../utils";
import { toDateTimeString } from "../../utils/formatDate";

type ConfigType = { language: string; defaultLanguage: string };

const getEventDetails = (
  event: CSSEvent,
  language: string,
  defaultLanguage: string
) => {
  if (!event.eventDetails) {
    return {};
  }
  return (
    event.eventDetails[language] || event.eventDetails[defaultLanguage] || {}
  );
};

const selectEventDetails = (config: ConfigType) => (event: CSSEvent) => ({
  ...event,
  eventDetails: getEventDetails(event, config.language, config.defaultLanguage),
});

const setRemainingSpots = (event: CSSEvent) => {
  const timeSlots = event.timeSlots || [];
  return {
    ...event,
    timeSlots: timeSlots.map((t: TimeSlot) => {
      let remainingSpots = Number.MAX_SAFE_INTEGER;
      if (t.registrationSettings?.maxRegistrationCount > 0) {
        remainingSpots =
          (t.registrationSettings?.maxRegistrationCount || 0) -
          (t.currentRegistrationCount || 0);
      }
      return {
        ...t,
        remainingSpots: Math.max(remainingSpots, 0),
      };
    }),
  };
};

function setRegistrationClosedDate(event: CSSEvent) {
  const timeSlots = event.timeSlots || [];
  const registrationCloseTime = event?.registrationSettings?.registrationCloseTime || 0;

  return {
    ...event,
    timeSlots: timeSlots.map((timeSlot) => {
      if (timeSlot.registrationClosedDate) {
        return timeSlot;
      }

      const registrationClosedDate = !registrationCloseTime ? toTimestamp(timeSlot.utcStartDate) : (
        toTimestamp(timeSlot.utcStartDate) - (registrationCloseTime * 1e3)
      );
      return {
        ...timeSlot,
        registrationClosedDate: toDateTimeString(new Date(registrationClosedDate)),
        utcRegistrationClosedDate: +new Date(registrationClosedDate) / 1e3
      };
    }),
  };
}

const sortTimeSlots = (event: CSSEvent) => {
  const timeSlots = event.timeSlots || [];

  return {
    ...event,
    timeSlots: timeSlots.sort((a: TimeSlot, b: TimeSlot) => {
      const aDate = new Date(toTimestamp(a.utcStartDate));
      const bDate = new Date(toTimestamp(b.utcStartDate));
      if (aDate > bDate) {
        return 1;
      } else if (aDate < bDate) {
        return -1;
      } else {
        return 0;
      }
    }),
  };
};

function setRegisterToWaitingList(event: CSSEvent) {
  const timeSlots = event.timeSlots || [];
  const waitingListEnabled = event.registrationSettings?.waitingListEnabled;

  return {
    ...event,
    timeSlots: timeSlots.map((t: TimeSlot) => {
      const { remainingSpots, currentWaitingListCount, registrationSettings } =
        t;

      let { maxWaitingListCount } = registrationSettings;
      if (maxWaitingListCount === 0) {
        maxWaitingListCount = Number.MAX_SAFE_INTEGER;
      }

      const registerToWaitingList =
        waitingListEnabled &&
        remainingSpots === 0 &&
        currentWaitingListCount < maxWaitingListCount;

      return {
        ...t,
        registerToWaitingList,
      };
    }),
  };
}

function filterActiveTimeSlots(event: CSSEvent) {
  const timeSlots = event.timeSlots || [];

  return {
    ...event,
    timeSlots: timeSlots.filter(
      (t) => {
        return t.visibilityState === ACTIVE && toTimestamp(t.utcEndDate) > Date.now();
      }
    ),
  };
}

function filterEventWithNoTimeSlots(event: CSSEvent) {
  return event.timeSlots.length > 0;
}

function filterPublishedEvents(event: CSSEvent) {
  const now = Date.now();
  return now > toTimestamp(event.utcVisibleAfter);
}

function setUrlId(event: CSSEvent) {
  const urlId = `${getSlug(event.eventDetails.eventName)}--${event.id}`;
  return { ...event, urlId };
}

export const toMap = (
  array: Array<{ [key: string]: any }>,
  key: string = "id"
) => array.reduce((acc, item) => ({ ...acc, [item[key]]: item }), {});

export const formatEvents = (
  events: Array<CSSEvent>,
  config: ConfigType,
  filterPastEvents: boolean = true
) => {
  events = events
    .filter(filterPublishedEvents)
    .map(filterActiveTimeSlots)
    .map(selectEventDetails(config))
    .map(setRegistrationClosedDate)
    .map(sortTimeSlots)
    .map(setRemainingSpots)
    .map(setRegisterToWaitingList)
    .map(setUrlId);

  if (filterPastEvents) {
    events = events.filter(filterEventWithNoTimeSlots);
  }

  return events;
};

export const byStore = (events: CSSEvent[]) => {
  return events.reduce((acc: ByStoreType, event) => {
    const storeNo = event?.location?.store?.storeNo;
    const isDigital = event?.location?.type === DIGITAL;

    if (isDigital) {
      return acc;
    }

    if (!storeNo) {
      return acc;
    }

    return { ...acc, [storeNo]: [...(acc[storeNo] || []), event.id] };
  }, {});
};

export const digitalEvents = (events: CSSEvent[]) => {
  return events.reduce((acc: string[], event) => {
    return event.location.type === DIGITAL ? [...acc, event.id] : acc;
  }, []);
};

export function getStoreDigits(storeNo: string) {
  const match = storeNo.match(/\d+/) || [];
  return match[0] || "";
}


export const _setRegisterToWaitingList = setRegisterToWaitingList;
export const _filterActiveTimeSlots = filterActiveTimeSlots;
export const _filterEventWithNoTimeSlots = filterEventWithNoTimeSlots;
export const _setRegistrationClosedDate = setRegistrationClosedDate;
export const _filterPublishedEvents = filterPublishedEvents;
export const _setRemainingSpots = setRemainingSpots;
